import { getPusherKeys } from 'config';
import Pusher from 'pusher-js';
import * as Sentry from '@sentry/react';

function getKeys() {
  const keys = getPusherKeys();
  return {
    pusherKey: keys.pusher_key,
    pusherCluster: keys.pusher_cluster,
  };
}

class PusherClient {
  private static instance: PusherClient = null;
  private pusherKey: string;
  private pusherCluster: string;
  private pusher: Pusher;
  private reconnectAttempt: number = 0;
  private baseReconnectDelay: number = 3000;
  private maxReconnectDelay: number = 30000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private static onReconnected: () => void;

  private constructor() {
    const keys = getKeys();
    this.pusherKey = keys.pusherKey;
    this.pusherCluster = keys.pusherCluster;

    this.pusher = new Pusher(this.pusherKey, {
      cluster: this.pusherCluster,
    });

    this.pusher.connection.bind('error', (error: string) => {
      console.error('Pusher connection error:', error);
      this.scheduleReconnect();
    });

    this.pusher.connection.bind('connected', () => {
      console.log('Pusher connected successfully.');
      this.reconnectAttempt = 0;
      if (this.reconnectTimer !== null) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }

      if (PusherClient.onReconnected) {
        PusherClient.onReconnected();
      }
    });

    this.pusher.connection.bind('unavailable', () => {
      console.warn('Pusher connection unavailable, attempting reconnection...');
      this.scheduleReconnect();
    });

    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  private handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      const state = this.pusher.connection.state;
      if (state !== 'connected' && state !== 'connecting') {
        console.log('Tab became visible, reconnecting Pusher...');
        this.reconnectAttempt = 0;
        if (this.reconnectTimer !== null) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
        this.pusher.connect();
      }
    }
  };

  public static getInstance(callback?: () => void): Pusher {
    if (!PusherClient.instance) {
      PusherClient.instance = new PusherClient();
    }
    if (callback) {
      PusherClient.onReconnected = callback;
    }
    return PusherClient.instance.pusher;
  }

  private scheduleReconnect() {
    if (this.reconnectTimer !== null) {
      return;
    }
    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempt),
      this.maxReconnectDelay,
    );
    this.reconnectAttempt++;
    console.log(`Pusher reconnection attempt ${this.reconnectAttempt} in ${delay}ms`);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.pusher.disconnect();
      this.pusher.connect();
    }, delay);
  }
}

export default PusherClient;
